import { defineBackendCommand } from '../contracts';
import type {
  ApplyProjectWritesRequest,
  ApplyProjectWritesResponse,
  ResolveProjectWriteConflictRequest,
  ResolveProjectWriteConflictResponse,
} from '@/features/sync/project-write-authority-api';
import type {
  JoinProjectResult,
  ProjectInvitePreviewResult,
} from '@/features/sync/share-api';
import type {
  BatchInviteData,
  CallSheetData,
  CustomAnnouncementData,
  DeadlinePingData,
  EmailResult,
  LineNotesData,
  LineNotesSendResult,
  MeetingInvitesData,
  ProjectInviteData,
  RehearsalReportData,
  RehearsalReportLinkData,
  RehearsalReportPostResult,
  RehearsalReportSendResult,
} from '@/features/email/types';
import type { ProjectScope } from '@/features/sync/project-scope';
import type { PermissionValue } from '@/features/auth/types';
import type {
  InventoryItem,
  InventoryItemInput,
  OrganizationUpdateData,
  PendingRosterUpdate,
  RosterPerson,
  RosterPersonInput,
  RosterWorkEntry,
  RoleTemplate,
  RoleTemplateInput,
} from '@/features/admin/org-types';
import type { FileNode, FilePolicy, FilePolicyAction } from '@/features/files/types';
import type { ScheduleEvent } from '@/features/scheduler/types';
import type {
  SubmitNoteRecoverySnapshotRequest,
  SubmitNoteRecoverySnapshotResponse,
} from '@/features/sync/backup-api';
import type { Asset } from '@/types';

interface SearchFreesoundRequest {
  query: string;
}

interface FreesoundSound {
  id: number;
  name: string;
  username: string;
  duration: number;
  previews: {
    'preview-hq-mp3': string;
    'preview-hq-ogg': string;
  };
}

type SearchFreesoundResponse = FreesoundSound[];

interface RequestInviteEmailUpdateRequest {
  orgId: string;
  projectId: string;
  invitationId: string;
  token: string;
  requestedEmail: string;
  note?: string;
}

interface RequestInviteEmailUpdateResponse {
  success?: boolean;
}

interface GetProjectInvitePreviewRequest {
  orgId: string;
  projectId: string;
  invitationId: string;
  token: string;
}

interface AcceptProjectInviteRequest {
  orgId: string;
  projectId: string;
  invitationId: string;
  token: string;
}

export interface CreateDailyRoomResponse {
  roomUrl: string;
  roomName: string;
  expiresAt: string;
}

export type CreateDailyRoomRequest = ProjectScope & {
  purpose?: 'video-meeting' | 'notes-transcription';
};

type GetDailyTokenRequest = ProjectScope & {
  roomName: string;
  userName: string;
};

interface GetDailyTokenResponse {
  token: string;
}

interface GetCalendarFeedTokenRequest {
  userId: string;
  projectId: string;
}

interface GetCalendarFeedTokenResponse {
  token: string | null;
}

interface CalendarFeedTokenMutationRequest {
  userId: string;
  orgId: string;
  projectId: string;
}

interface CalendarFeedTokenMutationResponse {
  token: string;
}

interface SendScheduleMeetingInvitesRequest {
  eventId: string;
  orgId: string;
  projectId: string;
  forceResend?: boolean;
}

export interface SendScheduleMeetingInvitesResponse {
  success: boolean;
  sent: number;
  total: number;
  message?: string;
}

interface ClearEmailDeliveryIssueRequest {
  orgId: string;
  email: string;
}

interface ClearEmailDeliveryIssueResponse {
  success?: boolean;
}

type GetAuthorizationLinkStatusRequest = Record<string, never>;

interface GetAuthorizationLinkStatusResponse {
  authUid: string;
  canonicalUserId: string;
  isAlias: boolean;
  currentProviderIds: string[];
  googleLinked: boolean;
}

type CreateAuthorizationLinkSessionRequest = Record<string, never>;

interface CreateAuthorizationLinkSessionResponse {
  sessionId: string;
  secret: string;
}

interface CompleteAuthorizationLinkSessionRequest {
  sessionId: string;
  secret: string;
}

interface CompleteAuthorizationLinkSessionResponse {
  success: true;
}

interface CompleteOrganizationOnboardingRequest {
  name?: string;
  logo?: string;
  kind: 'company';
}

interface CompleteOrganizationOnboardingResponse {
  orgId: string;
}

interface CommandSuccessResponse {
  success?: boolean;
}

type ProjectRoleAuthorityRequest = ProjectScope & {
  roleId: string;
};

type ProjectRoleDefinitionRequest = ProjectRoleAuthorityRequest & {
  name: string;
  description?: string;
  permissions: PermissionValue[];
};

type RoleTemplateInheritanceRequest = ProjectScope;

type ResetProjectRoleToTemplateRequest = ProjectRoleAuthorityRequest;

type LinkRosterPersonnelRequest = ProjectScope & {
  personnelId: string;
  rosterPersonId: string;
  projectTitle: string;
};

type UnlinkRosterPersonnelRequest = ProjectScope & {
  personnelId: string;
  rosterPersonId: string;
};

interface ResolveRosterPendingUpdateRequest {
  orgId: string;
  updateId: string;
  action: 'accepted' | 'dismissed';
}

interface CreateRosterPendingUpdateRequest {
  orgId: string;
  update: Omit<PendingRosterUpdate, 'id' | 'createdAt' | 'status'>;
}

type RosterAuthorityResponse = CommandSuccessResponse & {
  updateId?: string;
  count?: number;
};

interface UpdateOrganizationRequest {
  orgId: string;
  data: OrganizationUpdateData;
}

interface CreateRosterPersonRequest {
  orgId: string;
  person: RosterPersonInput;
}

interface CreateRosterPersonResponse {
  personId: string;
}

export type RosterPersonMutationData = Partial<Omit<RosterPerson, 'id' | 'createdAt' | 'updatedAt'>>;

interface UpdateRosterPersonRequest {
  orgId: string;
  personId: string;
  data: RosterPersonMutationData;
}

interface DeleteRosterPersonRequest {
  orgId: string;
  personId: string;
}

interface AssignRosterPersonToProductionRequest {
  orgId: string;
  personId: string;
  entry: RosterWorkEntry;
}

interface CreateRoleTemplateRequest {
  orgId: string;
  template: RoleTemplateInput;
}

interface CreateRoleTemplateResponse {
  templateId: string;
}

export type RoleTemplateMutationData = Partial<Omit<RoleTemplate, 'id' | 'createdAt' | 'updatedAt'>>;

interface UpdateRoleTemplateRequest {
  orgId: string;
  templateId: string;
  data: RoleTemplateMutationData;
}

interface DeleteRoleTemplateRequest {
  orgId: string;
  templateId: string;
}

interface CreateInventoryItemRequest {
  orgId: string;
  item: InventoryItemInput;
}

interface CreateInventoryItemResponse {
  itemId: string;
}

export type InventoryItemMutationData = Partial<Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>>;

interface UpdateInventoryItemRequest {
  orgId: string;
  itemId: string;
  data: InventoryItemMutationData;
}

interface DeleteInventoryItemRequest {
  orgId: string;
  itemId: string;
}

interface CheckoutInventoryItemRequest {
  orgId: string;
  itemId: string;
  projectId: string;
  projectTitle: string;
  role: string;
}

interface ReturnInventoryItemRequest {
  orgId: string;
  itemId: string;
}

interface LinkInventoryItemRequest {
  orgId: string;
  itemId: string;
  projectId: string;
  entityType: 'prop' | 'costume';
  entityId: string;
}

interface ImportProjectModulesRequest {
  sourceScope: ProjectScope;
  targetScope: ProjectScope;
  moduleIds: string[];
}

type ImportProjectModulesResponse = Array<{
  moduleId: string;
  success: boolean;
  count: number;
  error?: string;
}>;

type CreateAttendanceSignInTokenRequest = ProjectScope & {
  eventId: string;
  event: Omit<ScheduleEvent, 'id'>;
};

interface CreateAttendanceSignInTokenResponse {
  token: string;
}

export type DailySignInLinkSelectionMode = 'all-in-scope' | 'selected-events';

type DailySignInLinkWindowPayload = {
  scopeStartDate: string;
  scopeEndDate: string;
  opensBeforeMinutes: number;
  closesAfterMinutes: number;
  gracePeriodMinutes: number;
};

export type DailySignInLinkPayload = DailySignInLinkWindowPayload & (
  | {
      eventSelectionMode: Extract<DailySignInLinkSelectionMode, 'all-in-scope'>;
      selectedEventIds?: never;
    }
  | {
      eventSelectionMode: Extract<DailySignInLinkSelectionMode, 'selected-events'>;
      selectedEventIds: [string, ...string[]];
    }
);

type CreateDailyAttendanceSignInLinkRequest = ProjectScope & DailySignInLinkPayload;

type DailyAttendanceSignInLinkTokenResponse = {
  dailyLinkId: string;
  token: string;
};

export type DailySignInLinkManagementRow = {
  dailyLinkId: string;
  token: string;
  scopeStartDate: string;
  scopeEndDate: string;
  eventSelectionMode: DailySignInLinkSelectionMode;
  selectedEventIds?: string[];
  eventCount: number;
  opensBeforeMinutes: number;
  closesAfterMinutes: number;
  gracePeriodMinutes: number;
  createdAtMillis: number;
  updatedAtMillis: number;
};

type UpdateDailyAttendanceSignInLinkRequest = ProjectScope & DailySignInLinkPayload & {
  dailyLinkId: string;
};

type DailyAttendanceSignInLinkIdRequest = ProjectScope & {
  dailyLinkId: string;
};

type DailyAttendanceSignInLinkMutationResponse = {
  success: true;
};

type ListDailyAttendanceSignInLinksRequest = ProjectScope;

type ListDailyAttendanceSignInLinksResponse = {
  links: DailySignInLinkManagementRow[];
};

type GetAttendanceSignInEventRequest = ProjectScope & {
  token: string;
};

type SingleEventSignInEventResponse = ProjectScope & {
  kind: 'single-event';
  eventId: string;
  eventTitle: string;
  eventDate: string;
  eventStartTime: string;
  eventEndTime: string;
};

type DailySignInLinkEventResponse = {
  eventId: string;
  eventTitle: string;
  eventDate: string;
  eventStartTime: string;
  eventEndTime: string;
};

type DailySignInLinkResponse = ProjectScope & {
  kind: 'daily-link';
  dailyLinkId: string;
  eventSelectionMode: 'all-in-scope' | 'selected-events';
  opensBeforeMinutes: number;
  closesAfterMinutes: number;
  gracePeriodMinutes: number;
  events: DailySignInLinkEventResponse[];
};

type GetAttendanceSignInEventResponse = SingleEventSignInEventResponse | DailySignInLinkResponse;

type RecordAttendanceSignInRequest = ProjectScope & {
  token: string;
  selectedEventId?: string;
};

interface RecordAttendanceSignInResponse {
  status: 'present' | 'late';
  actorId: string;
  actorName: string;
  called: boolean;
  alreadySignedIn?: boolean;
}

type DismissProjectWelcomeCardRequest = ProjectScope & {
  memberUserId: string;
  memberRoleId?: string;
  personnelId?: string;
};

type DismissProjectMemberOnboardingRequest = ProjectScope & {
  memberUserId: string;
};

interface UserNotificationRequest {
  userId: string;
  notificationId: string;
}

type MarkAllUserNotificationsReadRequest = ProjectScope & {
  userId: string;
};

type SendProjectInviteCommandRequest = ProjectInviteData & {
  reissueInvitationId?: string;
};

type ProjectMemberRoleChangeRequest = ProjectScope & {
  userId: string;
  roleId: string;
};

type DeactivateProjectMemberRequest = ProjectScope & {
  userId: string;
};

type ReviewInviteEmailUpdateRequest = ProjectScope & {
  requestId: string;
};

interface ProjectFilesResponse {
  files?: FileNode[];
}

type SearchProjectFilesRequest = ProjectScope & {
  query: string;
};

type ListProjectFilesRequest = ProjectScope & {
  parentId?: string;
};

type RequestProjectFileAccessRequest = ProjectScope & {
  fileId: string;
  action: Extract<FilePolicyAction, 'access' | 'preview' | 'download'>;
  reason?: string;
  storageRef?: string;
};

interface RequestProjectFileAccessResponse {
  url?: unknown;
  expiresAt?: unknown;
  file?: FileNode;
  contentType?: unknown;
}

type CreateProjectFileUploadSessionRequest = ProjectScope & {
  name: string;
  mimeType: string;
  size: number;
};

interface CreateProjectFileUploadSessionResponse {
  uploadId?: unknown;
  fileId?: unknown;
  storagePath?: unknown;
  versionNumber?: unknown;
}

type FinalizeProjectFileUploadRequest = ProjectScope & {
  fileId: string;
  uploadId: string;
  storagePath: string;
  versionNumber: number;
  parentId: string;
  name: string;
  mimeType: string;
  size: number;
  policy: FilePolicy;
};

interface FinalizeProjectFileUploadResponse {
  file?: FileNode;
}

type ProjectFileMetadataChanges = Partial<Pick<
  FileNode,
  'name' | 'parentId' | 'versionNumber' | 'previousVersionId' | 'storageRef' | 'storagePath' | 'mimeType' | 'size'
>>;

type UpdateProjectFileMetadataRequest = ProjectScope & {
  fileId: string;
  changes: ProjectFileMetadataChanges;
};

type UpdateProjectFilePolicyRequest = ProjectScope & {
  fileId: string;
  policy: FilePolicy;
};

type ProjectFileIdRequest = ProjectScope & {
  fileId: string;
};

interface GenerateSvgAssetRequest {
  prompt: string;
}

interface SaveUserLibraryAssetRequest {
  userId: string;
  asset: Asset | Record<string, unknown>;
}

type TriggerScriptImportRequest = ProjectScope & {
  storagePath: string;
  importMode?: 'replace' | 'append';
  forceReOcr?: boolean;
};

interface TriggerScriptImportResponse {
  success: boolean;
  jobId: string;
  message?: string;
}

type ScopedProductionExtractionRequest = ProjectScope & {
  sourceJobId?: string;
  sceneIds?: string[];
  dryRun?: boolean;
};

interface ScopedProductionExtractionResponse {
  success: true;
  kind: 'props' | 'soundCues';
  runId: string;
  sourceJobId: string;
  artifactPath: string;
  count: number;
  extracted: number;
  persisted: number;
  dryRun: boolean;
  sourceFile: string;
  sceneIds: string[];
}

type BarryChatRequest = ProjectScope & {
  message: string;
  sessionId?: string;
  history?: Array<{ role: 'user' | 'model'; text: string }>;
};

export interface BarryChatResponse {
  response: string;
  toolsUsed: string[];
  modelMode?: 'managed' | 'byok';
  sessionId?: string;
}

type UpdateBarrySettingsRequest = ProjectScope & {
  byokApiKey: string;
};

interface UpdateBarrySettingsResponse {
  success: boolean;
}

export const firebaseFunctionCommands = {
  applyProjectWrites: defineBackendCommand<ApplyProjectWritesRequest, ApplyProjectWritesResponse>('applyProjectWrites'),
  resolveProjectWriteConflict: defineBackendCommand<
    ResolveProjectWriteConflictRequest,
    ResolveProjectWriteConflictResponse
  >('resolveProjectWriteConflict'),
  getAuthorizationLinkStatus: defineBackendCommand<
    GetAuthorizationLinkStatusRequest,
    GetAuthorizationLinkStatusResponse
  >('getAuthorizationLinkStatus'),
  createAuthorizationLinkSession: defineBackendCommand<
    CreateAuthorizationLinkSessionRequest,
    CreateAuthorizationLinkSessionResponse
  >('createAuthorizationLinkSession'),
  completeAuthorizationLinkSession: defineBackendCommand<
    CompleteAuthorizationLinkSessionRequest,
    CompleteAuthorizationLinkSessionResponse
  >('completeAuthorizationLinkSession'),
  completeOrganizationOnboarding: defineBackendCommand<
    CompleteOrganizationOnboardingRequest,
    CompleteOrganizationOnboardingResponse
  >('completeOrganizationOnboarding'),
  getProjectInvitePreview: defineBackendCommand<GetProjectInvitePreviewRequest, ProjectInvitePreviewResult>(
    'getProjectInvitePreview',
  ),
  requestInviteEmailUpdate: defineBackendCommand<
    RequestInviteEmailUpdateRequest,
    RequestInviteEmailUpdateResponse
  >('requestInviteEmailUpdate'),
  acceptProjectInvite: defineBackendCommand<AcceptProjectInviteRequest, JoinProjectResult>('acceptProjectInvite'),
  sendCallSheet: defineBackendCommand<CallSheetData, EmailResult>('sendCallSheet'),
  sendMeetingInvites: defineBackendCommand<MeetingInvitesData | SendScheduleMeetingInvitesRequest, EmailResult | SendScheduleMeetingInvitesResponse>(
    'sendMeetingInvites',
  ),
  getRehearsalReportLink: defineBackendCommand<RehearsalReportLinkData, RehearsalReportPostResult>(
    'getRehearsalReportLink',
  ),
  postRehearsalReport: defineBackendCommand<RehearsalReportData, RehearsalReportPostResult>('postRehearsalReport'),
  sendRehearsalReport: defineBackendCommand<RehearsalReportData, RehearsalReportSendResult>('sendRehearsalReport'),
  sendLineNotes: defineBackendCommand<LineNotesData, LineNotesSendResult>('sendLineNotes'),
  sendDeadlinePing: defineBackendCommand<DeadlinePingData, EmailResult>('sendDeadlinePing'),
  sendProjectInvite: defineBackendCommand<SendProjectInviteCommandRequest, EmailResult>('sendProjectInvite'),
  sendCustomAnnouncement: defineBackendCommand<CustomAnnouncementData, EmailResult>('sendCustomAnnouncement'),
  sendBatchInvitations: defineBackendCommand<BatchInviteData, EmailResult>('sendBatchInvitations'),
  clearEmailDeliveryIssue: defineBackendCommand<
    ClearEmailDeliveryIssueRequest,
    ClearEmailDeliveryIssueResponse
  >('clearEmailDeliveryIssue'),
  createDailyRoom: defineBackendCommand<CreateDailyRoomRequest, CreateDailyRoomResponse>('createDailyRoom'),
  getDailyToken: defineBackendCommand<GetDailyTokenRequest, GetDailyTokenResponse>('getDailyToken'),
  getCalendarFeedToken: defineBackendCommand<GetCalendarFeedTokenRequest, GetCalendarFeedTokenResponse>(
    'getCalendarFeedToken',
  ),
  generateCalendarFeedToken: defineBackendCommand<
    CalendarFeedTokenMutationRequest,
    CalendarFeedTokenMutationResponse
  >('generateCalendarFeedToken'),
  regenerateCalendarFeedToken: defineBackendCommand<
    CalendarFeedTokenMutationRequest,
    CalendarFeedTokenMutationResponse
  >('regenerateCalendarFeedToken'),
  searchFreesound: defineBackendCommand<SearchFreesoundRequest, SearchFreesoundResponse>('searchFreesound'),
  createProjectRole: defineBackendCommand<ProjectRoleDefinitionRequest, CommandSuccessResponse>('createProjectRole'),
  updateProjectRole: defineBackendCommand<ProjectRoleDefinitionRequest, CommandSuccessResponse>('updateProjectRole'),
  deleteProjectRole: defineBackendCommand<ProjectRoleAuthorityRequest, CommandSuccessResponse>('deleteProjectRole'),
  inheritProjectRoleTemplates: defineBackendCommand<RoleTemplateInheritanceRequest, RosterAuthorityResponse>(
    'inheritProjectRoleTemplates',
  ),
  resetProjectRoleToTemplate: defineBackendCommand<ResetProjectRoleToTemplateRequest, RosterAuthorityResponse>(
    'resetProjectRoleToTemplate',
  ),
  linkRosterPersonnel: defineBackendCommand<LinkRosterPersonnelRequest, RosterAuthorityResponse>(
    'linkRosterPersonnel',
  ),
  unlinkRosterPersonnel: defineBackendCommand<UnlinkRosterPersonnelRequest, RosterAuthorityResponse>(
    'unlinkRosterPersonnel',
  ),
  resolveRosterPendingUpdate: defineBackendCommand<ResolveRosterPendingUpdateRequest, RosterAuthorityResponse>(
    'resolveRosterPendingUpdate',
  ),
  createRosterPendingUpdate: defineBackendCommand<CreateRosterPendingUpdateRequest, RosterAuthorityResponse>(
    'createRosterPendingUpdate',
  ),
  updateOrganization: defineBackendCommand<UpdateOrganizationRequest, CommandSuccessResponse>(
    'updateOrganization',
  ),
  createRosterPerson: defineBackendCommand<CreateRosterPersonRequest, CreateRosterPersonResponse>(
    'createRosterPerson',
  ),
  updateRosterPerson: defineBackendCommand<UpdateRosterPersonRequest, CommandSuccessResponse>(
    'updateRosterPerson',
  ),
  deleteRosterPerson: defineBackendCommand<DeleteRosterPersonRequest, CommandSuccessResponse>(
    'deleteRosterPerson',
  ),
  assignRosterPersonToProduction: defineBackendCommand<
    AssignRosterPersonToProductionRequest,
    CommandSuccessResponse
  >('assignRosterPersonToProduction'),
  createRoleTemplate: defineBackendCommand<CreateRoleTemplateRequest, CreateRoleTemplateResponse>(
    'createRoleTemplate',
  ),
  updateRoleTemplate: defineBackendCommand<UpdateRoleTemplateRequest, CommandSuccessResponse>(
    'updateRoleTemplate',
  ),
  deleteRoleTemplate: defineBackendCommand<DeleteRoleTemplateRequest, CommandSuccessResponse>(
    'deleteRoleTemplate',
  ),
  createInventoryItem: defineBackendCommand<CreateInventoryItemRequest, CreateInventoryItemResponse>(
    'createInventoryItem',
  ),
  updateInventoryItem: defineBackendCommand<UpdateInventoryItemRequest, CommandSuccessResponse>(
    'updateInventoryItem',
  ),
  deleteInventoryItem: defineBackendCommand<DeleteInventoryItemRequest, CommandSuccessResponse>(
    'deleteInventoryItem',
  ),
  checkoutInventoryItem: defineBackendCommand<CheckoutInventoryItemRequest, CommandSuccessResponse>(
    'checkoutInventoryItem',
  ),
  returnInventoryItem: defineBackendCommand<ReturnInventoryItemRequest, CommandSuccessResponse>(
    'returnInventoryItem',
  ),
  linkInventoryItem: defineBackendCommand<LinkInventoryItemRequest, CommandSuccessResponse>(
    'linkInventoryItem',
  ),
  importProjectModules: defineBackendCommand<ImportProjectModulesRequest, ImportProjectModulesResponse>(
    'importProjectModules',
  ),
  createAttendanceSignInToken: defineBackendCommand<
    CreateAttendanceSignInTokenRequest,
    CreateAttendanceSignInTokenResponse
  >('createAttendanceSignInToken'),
  createDailyAttendanceSignInLink: defineBackendCommand<
    CreateDailyAttendanceSignInLinkRequest,
    DailyAttendanceSignInLinkTokenResponse
  >('createDailyAttendanceSignInLink'),
  updateDailyAttendanceSignInLink: defineBackendCommand<
    UpdateDailyAttendanceSignInLinkRequest,
    DailyAttendanceSignInLinkMutationResponse
  >('updateDailyAttendanceSignInLink'),
  listDailyAttendanceSignInLinks: defineBackendCommand<
    ListDailyAttendanceSignInLinksRequest,
    ListDailyAttendanceSignInLinksResponse
  >('listDailyAttendanceSignInLinks'),
  replaceDailyAttendanceSignInLink: defineBackendCommand<
    DailyAttendanceSignInLinkIdRequest,
    DailyAttendanceSignInLinkTokenResponse
  >('replaceDailyAttendanceSignInLink'),
  revokeDailyAttendanceSignInLink: defineBackendCommand<
    DailyAttendanceSignInLinkIdRequest,
    DailyAttendanceSignInLinkMutationResponse
  >('revokeDailyAttendanceSignInLink'),
  getAttendanceSignInEvent: defineBackendCommand<
    GetAttendanceSignInEventRequest,
    GetAttendanceSignInEventResponse
  >('getAttendanceSignInEvent'),
  recordAttendanceSignIn: defineBackendCommand<RecordAttendanceSignInRequest, RecordAttendanceSignInResponse>(
    'recordAttendanceSignIn',
  ),
  dismissProjectWelcomeCard: defineBackendCommand<DismissProjectWelcomeCardRequest, CommandSuccessResponse>(
    'dismissProjectWelcomeCard',
  ),
  dismissProjectMemberOnboarding: defineBackendCommand<
    DismissProjectMemberOnboardingRequest,
    CommandSuccessResponse
  >('dismissProjectMemberOnboarding'),
  markUserNotificationRead: defineBackendCommand<UserNotificationRequest, CommandSuccessResponse>(
    'markUserNotificationRead',
  ),
  markAllUserNotificationsRead: defineBackendCommand<
    MarkAllUserNotificationsReadRequest,
    CommandSuccessResponse
  >('markAllUserNotificationsRead'),
  resolveUserNotification: defineBackendCommand<UserNotificationRequest, CommandSuccessResponse>(
    'resolveUserNotification',
  ),
  deactivateProjectMember: defineBackendCommand<DeactivateProjectMemberRequest, CommandSuccessResponse>(
    'deactivateProjectMember',
  ),
  changeProjectMemberRole: defineBackendCommand<ProjectMemberRoleChangeRequest, CommandSuccessResponse>(
    'changeProjectMemberRole',
  ),
  approveInviteEmailUpdate: defineBackendCommand<ReviewInviteEmailUpdateRequest, CommandSuccessResponse>(
    'approveInviteEmailUpdate',
  ),
  rejectInviteEmailUpdate: defineBackendCommand<ReviewInviteEmailUpdateRequest, CommandSuccessResponse>(
    'rejectInviteEmailUpdate',
  ),
  listProjectFiles: defineBackendCommand<ListProjectFilesRequest, ProjectFilesResponse>('listProjectFiles'),
  searchProjectFiles: defineBackendCommand<SearchProjectFilesRequest, ProjectFilesResponse>('searchProjectFiles'),
  listProjectTrashedFiles: defineBackendCommand<ProjectScope, ProjectFilesResponse>('listProjectTrashedFiles'),
  requestProjectFileAccess: defineBackendCommand<
    RequestProjectFileAccessRequest,
    RequestProjectFileAccessResponse
  >('requestProjectFileAccess'),
  createProjectFileUploadSession: defineBackendCommand<
    CreateProjectFileUploadSessionRequest,
    CreateProjectFileUploadSessionResponse
  >('createProjectFileUploadSession'),
  finalizeProjectFileUpload: defineBackendCommand<
    FinalizeProjectFileUploadRequest,
    FinalizeProjectFileUploadResponse
  >('finalizeProjectFileUpload'),
  updateProjectFileMetadata: defineBackendCommand<UpdateProjectFileMetadataRequest, CommandSuccessResponse>(
    'updateProjectFileMetadata',
  ),
  updateProjectFilePolicy: defineBackendCommand<UpdateProjectFilePolicyRequest, CommandSuccessResponse>(
    'updateProjectFilePolicy',
  ),
  moveProjectFileToTrash: defineBackendCommand<ProjectFileIdRequest, CommandSuccessResponse>(
    'moveProjectFileToTrash',
  ),
  restoreProjectFileFromTrash: defineBackendCommand<ProjectFileIdRequest, CommandSuccessResponse>(
    'restoreProjectFileFromTrash',
  ),
  saveNoteRecoverySnapshot: defineBackendCommand<
    SubmitNoteRecoverySnapshotRequest,
    SubmitNoteRecoverySnapshotResponse
  >('saveNoteRecoverySnapshot'),
  generateSvgAsset: defineBackendCommand<GenerateSvgAssetRequest, unknown>('generateSvgAsset', {
    timeoutMs: 120_000,
  }),
  saveUserLibraryAsset: defineBackendCommand<SaveUserLibraryAssetRequest, CommandSuccessResponse>(
    'saveUserLibraryAsset',
  ),
  startOcrJob: defineBackendCommand<TriggerScriptImportRequest, TriggerScriptImportResponse>('startOcrJob'),
  startMetadataExtraction: defineBackendCommand<TriggerScriptImportRequest, TriggerScriptImportResponse>(
    'startMetadataExtraction',
  ),
  extractPropsFromScript: defineBackendCommand<
    ScopedProductionExtractionRequest,
    ScopedProductionExtractionResponse
  >('extractPropsFromScript', { timeoutMs: 540_000 }),
  extractSoundCuesFromScript: defineBackendCommand<
    ScopedProductionExtractionRequest,
    ScopedProductionExtractionResponse
  >('extractSoundCuesFromScript', { timeoutMs: 540_000 }),
  barryChat: defineBackendCommand<BarryChatRequest, BarryChatResponse>('barryChat', {
    timeoutMs: 120_000,
  }),
  updateBarrySettings: defineBackendCommand<UpdateBarrySettingsRequest, UpdateBarrySettingsResponse>(
    'updateBarrySettings',
  ),
} as const;
